import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  HStack,
  useToast
} from '@chakra-ui/react';
import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { seasonChallengeSchema, SeasonChallengeFormData } from '@olagg/validation-schemas/seasonChallenge.schema';
import { AdminSeasonChallenge as SeasonChallenge } from '@olagg/db-types';
import OlaGGFormInput from '../../../components/Forms/OlaggFormInput';
import OlaggForm from '../../../components/Forms/OlaggForm';

type IProps = {
  initialData?: SeasonChallenge | undefined;
  seasonId: string;
  onSubmit: (data: SeasonChallengeFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

type MultilingualTitle = {
  es: string;
  en?: string;
  pt?: string;
};

type MultilingualDescription = {
  es?: string;
  en?: string;
  pt?: string;
};

const buildTitle = (challenge?: SeasonChallenge): MultilingualTitle => ({
  es: challenge?.title|| '',
  en: challenge?.extraData?.en?.title || '',
  pt: challenge?.extraData?.pt?.title || ''
});

const buildDescription = (challenge?: SeasonChallenge): MultilingualDescription => ({
  es: challenge?.description || '',
  en: challenge?.extraData?.en?.description || '',
  pt: challenge?.extraData?.pt?.description || ''
});

const ChallengeForm: FC<IProps> = ({ 
  initialData, 
  seasonId, 
  onSubmit, 
  onCancel, 
  loading = false, 
  isEdit = false 
}) => {
  const toast = useToast();
  const [title, setTitle] = useState<MultilingualTitle>(buildTitle(initialData));
  const [description, setDescription] = useState<MultilingualDescription>(buildDescription(initialData));
  const [otherLangs, setOtherLangs] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );

  const [hasTranslations, setHasTranslations] = useState<boolean>(
    !!(initialData?.extraData?.en?.title || initialData?.extraData?.pt?.title || 
       initialData?.extraData?.en?.description || initialData?.extraData?.pt?.description)
  );

  const checkForTranslations = () => {
    const hasEnglish = !!(title.en || description.en);
    const hasPortuguese = !!(title.pt || description.pt);
    const hasExistingTranslations = !!(initialData?.extraData?.en || initialData?.extraData?.pt);
    
    return hasEnglish || hasPortuguese || hasExistingTranslations;
  };

  useEffect(() => {
    setHasTranslations(checkForTranslations());
  }, [title, description]);

  const useFormMethods = useForm<SeasonChallengeFormData>({
    resolver: yupResolver(seasonChallengeSchema),
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      dayNumber: initialData?.dayNumber || 1,
      clawCoinsReward: initialData?.clawCoinsReward || 1,
      gameSeasonId: parseInt(seasonId),
      extra_data: initialData?.extraData || {}
    }
  });

  const beforeSubmit = async (data: SeasonChallengeFormData) => {
    const finalData: SeasonChallengeFormData = {
      ...data,
      title: title.es,
      description: description.es,
      gameSeasonId: parseInt(seasonId),
      extra_data: {
        ...data.extra_data,
        ...(hasTranslations && {
          en: {
            title: title.en || '',
            description: description.en || '',
          },
          pt: {
            title: title.pt || '',
            description: description.pt || '',
          },
          translated: true
        })
      }
    };
    await onSubmit(finalData);
  };

  return (
    <Flex w={'60vw'} mx={4} pb={4} minH='600px' justifyContent='center' direction="column">
      {/* Switch para otros idiomas */}
      <Flex
        w="full"
        justify="start"
        align="center"
        style={{ marginBottom: '20px' }}
      >
        <Text color="white" style={{ cursor: 'pointer' }}>
          Otros idiomas
        </Text>
        <Switch
          colorScheme="purple"
          isChecked={otherLangs}
          size="md"
          ml={2}
          onChange={() => setOtherLangs(prev => !prev)}
        />
      </Flex>

      <OlaggForm
        onSubmit={beforeSubmit}
        useFormMethods={useFormMethods}
        style={{
          width: '100%'
        }}
      >
        {/* Título en Español */}
        <OlaGGFormInput
          label='Título del Desafío'
          controlName='title'
          placeholder='Título del desafío'
          required={true}
          onChange={(e) => {
            setTitle({ ...title, es: e.target.value });
          }}
        />

        {/* Título en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Título en inglés */}
            <OlaGGFormInput
              label='Título en Inglés'
              controlName='extra_data.title.en'
              placeholder='Challenge Title in English'
              onChange={(e) => setTitle({ ...title, en: e.target.value })}
            />

            {/* Título en portugués */}
            <OlaGGFormInput
              label='Título en Portugués'
              controlName='extra_data.title.pt'
              placeholder='Título do Desafio em Português'
              onChange={(e) => setTitle({ ...title, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Número de día */}
        <OlaGGFormInput
          label='Número de Día'
          description='Número de día en el que se debe completar la tarea'
          controlName='dayNumber'
          placeholder='1'
          inputType='number'
          required={true}
        />

        {/* Número de Claw Coins */}
        <OlaGGFormInput
          label='Número de Claw Coins'
          description='Número de Claw Coins que se otorgarán al completar la tarea'
          controlName='clawCoinsReward'
          placeholder='1'
          inputType='number'
          required={true}
        />

        {/* Descripción */}
        <OlaGGFormInput
          label='Descripción'
          controlName='description'
          placeholder='Descripción del desafío'
          inputType='textarea'
          onChange={(e) => setDescription({ ...description, es: e.target.value })}
          required={true}
        />

        {/* Descripción en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Descripción en inglés */}
            <OlaGGFormInput
              label='Descripción en Inglés'
              controlName='extra_data.description.en'
              placeholder='Challenge Description in English'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, en: e.target.value })}
            />

            {/* Descripción en portugués */}
            <OlaGGFormInput
              label='Descripción en Portugués'
              controlName='extra_data.description.pt'
              placeholder='Descrição do Desafio em Português'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Botones */}
        <HStack justify="flex-end" spacing={4} mt={6}>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={onCancel}
            isDisabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant={'filledGradient'}
            type="submit"
            isLoading={loading}
            loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
          >
            {isEdit ? 'Actualizar Desafío' : 'Crear Desafío'}
          </Button>
        </HStack>
      </OlaggForm>
    </Flex>
  );
};

export default ChallengeForm;
export { ChallengeForm as Form };
